import axios from "axios";
import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";
import axiosInstance from "../utils/axios";
import { isRememberMe, isValidToken, refreshAccessToken, setSession } from "../utils/jwt";
import { initiateSSOToApp } from "../utils/sso";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isAdmin: false,
  isSubAdmin: false,
  user: null,
};

const handlers = {
  RESET_CART: (state) => {
    return {
      ...state,
      user: { ...state.user, cartCount: 0 },
    };
  },

  INCREMENT_CART: (state, { payload }) => {
    return {
      ...state,
      user: { ...state.user, cartCount: payload },
    };
  },

  DECREMENT_CART: (state, { payload }) => {
    return {
      ...state,
      user: { ...state.user, cartCount: state.user.cartCount - payload },
    };
  },

  SET_PROFILE_IMAGE: (state, { payload }) => {
    return {
      ...state,
      user: {
        ...state.user,
        user_profile: {
          ...state.user?.user_profile,
          profile_image: payload,
        },
      },
    };
  },
  SET_IMAGE: (state, { payload }) => {
    return {
      ...state,
      user: {
        ...state.user,
        user_profile: {
          ...state.user?.user_profile,
          cover_image: payload,
        },
      },
    };
  },
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      isAdmin: JSON.parse(localStorage.getItem("isAdmin")),
      isSubAdmin: JSON.parse(localStorage.getItem("isSubAdmin")),
      user,
    };
  },

  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isAdmin: Boolean(user.is_super_admin),
      isSubAdmin: Boolean(user.is_sub_admin),
      user,
    };
  },

  GET_USER: (state, action) => {
    const {
      scope_email,
      scope_facebook,
      scope_instagram,
      scope_medium,
      scope_phone,
      scope_telegram,
      scope_twitter,
      scope_whatsapp,
      referral_count,
      ...rest
    } = action.payload.user;
    return {
      ...state,
      isAuthenticated: true,
      isAdmin: action.payload.isAdmin,
      isSubAdmin: action.payload.isSubAdmin,
      user: {
        ...rest,
        social: {
          scope_email,
          scope_facebook,
          scope_instagram,
          scope_medium,
          scope_phone,
          scope_telegram,
          scope_twitter,
          scope_whatsapp,
        },
      },
    };
  },

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isAdmin: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: () => Promise.resolve(),
  twoFactorVerify: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  getUser: () => Promise.resolve(),
  setImage: () => Promise.resolve(),
  setProfileImage: () => Promise.resolve(),
  incrementCart: () => Promise.resolve(),
  decrementCart: () => Promise.resolve(),
  resetCart: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        // CRITICAL: Check for logout page BEFORE reading localStorage
        console.log('[JWTContext] Initialize called');
        console.log('[JWTContext] Current pathname:', window.location.pathname);

        // If on /auth/logout page, clear localStorage immediately
        if (window.location.pathname === '/auth/logout') {
          console.log('[JWTContext] Logout page detected - clearing ALL localStorage');

          // Il token va ANNULLATO sul server, non solo buttato via. Questa pagina
          // e' l'uscita che usa l'app (rinvio dal browser, finestrella nascosta
          // della PWA installata, «accedi con un altro account»): finora svuotava
          // solo localStorage e il token restava valido fino alla sua scadenza.
          // Quanto vale quella scadenza dipende da COME e' nato il token che il
          // pannello ha in mano: form login o 2FA => token Tymon, JWT_TTL=60 min
          // e rinnovabile via api/refresh fino a JWT_REFRESH_TTL (14 giorni);
          // rientro dall'app via SSO (AuthCallback) => token di IssuesAppJwt,
          // JWT_APP_ACCESS_TTL (30 min in prod) e NON rinnovabile, perche'
          // AuthCallback scarta il refresh_token dell'exchange.
          // Il menu «Esci» del pannello revoca da sempre (logout() piu' sotto):
          // qui mancava.
          //
          // Perche' QUI e non in pages/auth/Logout.jsx: questo blocco svuota
          // localStorage per primo (gira su initialize, prima dell'effect della
          // pagina), quindi la' il token non esiste piu'.
          // Perche' fetch e non axiosInstance: l'interceptor su 401 tenterebbe un
          // refresh proprio mentre stiamo uscendo. `keepalive` perche' l'app
          // smonta la finestrella ~400 ms dopo onload e una richiesta normale
          // morirebbe con lei. Best-effort: nessun errore deve bloccare la
          // pulizia, o l'utente resta intrappolato nella pagina di uscita.
          const tokenToRevoke = localStorage.getItem("accessToken");
          if (tokenToRevoke) {
            try {
              const base = axiosInstance.defaults.baseURL || "";
              const logoutUrl = `${base}${base.endsWith("/") ? "" : "/"}api/logout`;
              fetch(logoutUrl, {
                method: "POST",
                headers: { Authorization: `Bearer ${tokenToRevoke}` },
                keepalive: true,
              }).catch(() => {
                console.warn('[JWTContext] Revoca del token non riuscita (non bloccante)');
              });
              console.log('[JWTContext] Revoca del token richiesta al server');
            } catch (err) {
              console.warn('[JWTContext] Revoca del token non partita (non bloccante)');
            }
          }

          // Preserve lastLoginEmail so the login form stays pre-filled
          const emailToKeep = localStorage.getItem("lastLoginEmail");
          localStorage.clear();
          if (emailToKeep) localStorage.setItem("lastLoginEmail", emailToKeep);

          // Initialize as not authenticated
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });

          // Redirect to login after a short delay
          setTimeout(() => {
            console.log('[JWTContext] Redirecting to login');
            window.location.href = '/auth/login';
          }, 100);

          return; // Exit early - don't check token
        }

        let accessToken = window.localStorage.getItem("accessToken");

        // If token is expired but rememberMe=true, try one silent refresh
        // (backend allows refresh within JWT_REFRESH_TTL=14 days).
        if (accessToken && !isValidToken(accessToken) && isRememberMe()) {
          console.log('[JWTContext] Token expired but rememberMe=true → attempting silent refresh');
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            console.log('[JWTContext] Silent refresh OK');
            accessToken = refreshed;
          } else {
            console.log('[JWTContext] Silent refresh failed');
          }
        }

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const user = {};
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          // Token invalid or missing
          console.log('[JWTContext] Token invalid or missing');

          // Clear invalid auth data
          if (accessToken) {
            console.log('[JWTContext] Token invalid - clearing localStorage');
            localStorage.removeItem("accessToken");
            localStorage.removeItem("isAdmin");
            localStorage.removeItem("isSubAdmin");
            localStorage.removeItem("isImpersonate");
            localStorage.removeItem("source_id");
            localStorage.removeItem("menu");
            localStorage.removeItem("rememberMe");
            setSession(null);
          }

          // Check if coming from App - redirect back to App to request SSO token
          const urlParams = new URLSearchParams(window.location.search);
          const fromApp = urlParams.get('from') === 'app';

          if (fromApp && !window.location.pathname.includes('/auth-callback')) {
            console.log('[JWTContext] Coming from App without token - redirecting to App for SSO');
            const appUrl = (process.env.REACT_APP_APP_URL || 'http://app.summus_network.localhost').replace(/\/$/, '');
            const networkUrl = (process.env.REACT_APP_NETWORK_URL || 'http://summus_network.localhost').replace(/\/$/, '');
            const returnUrl = `${networkUrl}${window.location.pathname}`;

            // Redirect to App's SSO endpoint to get a token
            const ssoRequestUrl = `${appUrl}/sso-request?returnUrl=${encodeURIComponent(returnUrl)}&target=network`;
            console.log('[JWTContext] Redirecting to:', ssoRequestUrl);
            window.location.href = ssoRequestUrl;
            return; // Don't dispatch INITIALIZE - we're redirecting
          }

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        // Error during initialization - clear all auth data
        console.log('[JWTContext] Initialization error - clearing localStorage');
        localStorage.removeItem("accessToken");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("isSubAdmin");
        localStorage.removeItem("isImpersonate");
        localStorage.removeItem("source_id");
        localStorage.removeItem("menu");
        localStorage.removeItem("rememberMe");
        setSession(null);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password, remember) => {
    console.log('[JWTContext.login] Login function called');
    console.log('[JWTContext.login] Current URL:', window.location.href);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);
    formData.append("remember", remember);

    try {
      const URL = `${process.env.REACT_APP_HOST_NAME}api/login`;
      const { status, data } = await axios.post(URL, formData);
      if (status === 200) {
        const { access_token, user, message } = data;
        // Verifica in due passaggi attiva: password giusta, ma serve il codice.
        // Il backend NON manda piu' il segreto (lo faceva: chi indovinava la
        // password poteva generarsi i codici da solo) — solo un challenge
        // opaco valido 5 minuti, che twoFactorVerify scambia con il token.
        if (data?.two_factor_required) {
          return { status: false, twoFactorRequired: true, challenge: data.challenge };
        }
        if (!message) {
          localStorage.setItem("isAdmin", Boolean(user.is_super_admin));
          localStorage.setItem("isSubAdmin", Boolean(user.is_sub_admin));
          try { localStorage.setItem("lastLoginEmail", email); } catch {}
          try { localStorage.setItem("rememberMe", remember ? "1" : "0"); } catch {}
          setSession(access_token);
          dispatch({
            type: "LOGIN",
            payload: {
              user,
            },
          });

          console.log('[JWTContext.login] Login successful, returning data');
          console.log('[JWTContext.login] URL after dispatch:', window.location.href);

          return { status: true, data: user.username };
        } else {
          return { status: false, message };
        }
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Network error";
      return { status: false, message };
    }

    return "";
  };

  /**
   * Secondo passo del login: challenge (monouso, 5 min) + codice dell'app di
   * autenticazione oppure codice di recupero. A esito positivo apre la
   * sessione esattamente come il login a un passo.
   */
  const twoFactorVerify = async (challenge, code, email, remember) => {
    const formData = new FormData();
    formData.append("challenge", challenge);
    formData.append("code", code);

    try {
      const URL = `${process.env.REACT_APP_HOST_NAME}api/twofaverify`;
      const { status, data } = await axios.post(URL, formData);
      if (status === 200 && data?.access_token) {
        const { access_token, user } = data;
        localStorage.setItem("isAdmin", Boolean(user.is_super_admin));
        localStorage.setItem("isSubAdmin", Boolean(user.is_sub_admin));
        try { localStorage.setItem("lastLoginEmail", email); } catch {}
        try { localStorage.setItem("rememberMe", remember ? "1" : "0"); } catch {}
        setSession(access_token);
        dispatch({ type: "LOGIN", payload: { user } });
        return { status: true, data: user.username };
      }
      return { status: false, message: data?.message || "invalid_code" };
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Network error";
      return { status: false, message };
    }
  };

  const register = async (email, username,name,surname, password, referral, termsVersion) => {
    const formData = new FormData();
    formData.append("email", email);
    formData.append("username", username);
    formData.append("name", name);
    formData.append("surname", surname);

    formData.append("password", password);
    formData.append("referral", referral);
    // Versione T&C accettata: prova di accettazione registrata lato server.
    if (termsVersion) formData.append("terms_version", termsVersion);

    const response = await axiosInstance.post("api/register", formData);
    const { access_token, user } = response.data;

    localStorage.setItem("isAdmin", Boolean(user.is_super_admin));
    localStorage.setItem("isSubAdmin", Boolean(user.is_sub_admin));
    setSession(access_token);

    // Entry "a freddo" (piano A — auto-chain): se l'utente e' arrivato da
    // app.summus.life/start (Register ha settato il flag vedendo ?app_sso=1),
    // facciamo SSO diretto nell'app saltando l'hub. setSession ha gia' messo il
    // token sull'header axios, quindi create-session (autenticata) funziona.
    let ssoToAppAfterRegister = null;
    try {
      ssoToAppAfterRegister = sessionStorage.getItem("ssoToAppAfterRegister");
    } catch {
      /* sessionStorage non disponibile */
    }
    if (ssoToAppAfterRegister) {
      try {
        sessionStorage.removeItem("ssoToAppAfterRegister");
      } catch {
        /* noop */
      }
      // PKCE → redirect a app/auth-callback → /dashboard → onboarding.
      await initiateSSOToApp();
      return;
    }

    // Check if there's a post-registration redirect (e.g., product purchase)
    const postRegisterRedirect = sessionStorage.getItem("postRegisterRedirect");
    if (postRegisterRedirect) {
      sessionStorage.removeItem("postRegisterRedirect");
      window.location = postRegisterRedirect;
    } else {
      window.location = "/";
    }

    // window.localStorage.setItem("accessToken", access_token);
    // dispatch({
    //   type: "REGISTER",
    //   payload: {
    //     user,
    //   },
    // });
  };

  const setImage = async (image) => {
    dispatch({ type: "SET_IMAGE", payload: image });
  };

  const logout = async () => {
    try {
      console.log('[Network] 🚪 Logout initiated');

      const { status } = await axiosInstance.post("api/logout");
      if (status === 200) {
        console.log('[Network] ✅ Backend logout successful');

        // Step 1: Trigger logout on summus_app via hidden iframe
        console.log('[Network] 🔄 Triggering App logout via iframe...');
        const appUrl = process.env.REACT_APP_APP_URL || 'http://app.summus_network.localhost';
        const appLogoutUrl = `${appUrl}/auth/logout?silent=true`;

        // Create hidden iframe to trigger app logout
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = appLogoutUrl;
        document.body.appendChild(iframe);

        console.log('[Network] ✅ App logout iframe created:', appLogoutUrl);

        // Give iframe 2 seconds to complete, then continue with Network logout
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Remove iframe
        document.body.removeChild(iframe);
        console.log('[Network] 🧹 Iframe removed');

        // Step 2: Clear Network localStorage
        console.log('[Network] 🧹 Clearing Network localStorage');
        setSession(null);
        localStorage.removeItem("menu");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("isSubAdmin");
        localStorage.removeItem("isImpersonate");
        localStorage.removeItem("source_id");
        localStorage.removeItem("rememberMe");

        dispatch({ type: "LOGOUT" });
        console.log('[Network] ✅ Logout complete');
      }
    } catch (err) {
      console.error('[Network] ❌ Logout error:', err);
    }
  };

  const resetPassword = async (reqData) => {
    const data = new FormData();
    data.append("email", reqData.email);
    data.append("password", reqData.password);
    data.append("password_confirmation", reqData.repassword);
    data.append("token", reqData.token);
    try {
      const res = await axiosInstance.post(`/reset-password`, data);
      return res.status === 200;
    } catch (error) {}
  };

  const getUser = async () => {
    try {
      const { status, data } = await axiosInstance.get("api/profile");
      if (status === 200) {
        const {
          user,
          cart_count,
          referral_count,
          balance_amount,
          total_commission,
          total_payout,
          sponsor_info,
        } = data.data;

        const sponsorInfo = {
          name: [sponsor_info?.user_profile?.first_name, sponsor_info?.user_profile?.last_name].filter(Boolean).join(' '),
          email: sponsor_info?.email,
          phone: sponsor_info?.user_profile?.mobile,
        };

        if (user)
          dispatch({
            type: "GET_USER",
            payload: {
              user: {
                ...user,
                cartCount: cart_count,
                referralCount: referral_count,
                balanceAmount: parseInt(balance_amount),
                totalCommission: parseInt(total_commission),
                totalPayout: parseInt(total_payout),
                sponsorInfo,
              },
              isSubAdmin: JSON.parse(localStorage.getItem("isSubAdmin")),
              isAdmin: JSON.parse(localStorage.getItem("isAdmin")),
            },
          });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const setProfileImage = (newUri) => {
    dispatch({ type: "SET_PROFILE_IMAGE", payload: newUri });
  };

  const incrementCart = (cartCount) => {
    dispatch({ type: "INCREMENT_CART", payload: cartCount });
  };

  const decrementCart = () => {
    dispatch({ type: "DECREMENT_CART", payload: 1 });
  };

  const resetCart = () => {
    dispatch({ type: "RESET_CART" });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        twoFactorVerify,
        logout,
        register,
        resetPassword,
        getUser,
        setImage,
        setProfileImage,
        incrementCart,
        decrementCart,
        resetCart,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
